import { useCallback, useMemo, useState } from 'react';

import { mapSelectOptionToFilters, mapValuesToSelect } from 'Components/VehicleList/filters/Filters.helpers';
import { sortModels } from 'Components/VehicleList/filters/ModelFilter/ModelMultiSelect.helpers';
import { formatAsSelectOptions } from 'Components/VehicleList/VehicleList.helpers';
import { useFilters } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useGetMakesBySlugs, useMakes } from 'Stores/MakesStore/MakesStore';
import { useGetModels, useGetModelsById, useGetModelsByMakeIds } from 'Stores/ModelsStore/ModelsStore';
import { useValueChangedCount } from 'Utilities/hooks/useValueChangedCount';
import { filterModelsByMakes, getMakesIds } from 'Utilities/makeModel/makeModel.helpers';

interface UseMakeModelFilterOptions {
	autoCommit?: boolean;
	onCommit?: (makes: string[], models: number[]) => void;
}

interface UseMakeModelFilterReturn {
	formattedMakes: Array<{ text: string; value: string }>;
	formattedModels: Array<{ text: string; value: number }>;
	handleMakeChange: (event: { value: Array<{ value: string }> }) => void;
	handleModelChange: (event: { value: Array<{ value: number }> }) => void;
	isModelDisabled: boolean;
	localMake: string[];
	localModel: number[];
	makeOptionsChangedCount: number;
	makeValues: Array<{ text: string; value: string }>;
	modelOptionsChangedCount: number;
	modelValues: Array<{ text: string; value: number }>;
}

export const useMakeModelFilter = ({
	autoCommit = true,
	onCommit,
}: UseMakeModelFilterOptions = {}): UseMakeModelFilterReturn => {
	const filters = useFilters();
	const makes = useMakes();
	const getMakesBySlugs = useGetMakesBySlugs();
	const allModels = useGetModels();
	const getModelsByMakeIds = useGetModelsByMakeIds();
	const getModelsById = useGetModelsById();

	const [localMake, setLocalMake] = useState<string[]>(filters.make);
	const [localModel, setLocalModel] = useState<number[]>(filters.model);

	const formattedMakes = useMemo(
		() =>
			formatAsSelectOptions({
				options: makes,
				text: 'name',
				textSuffix: 'total',
				value: 'slug',
			}),
		[makes],
	);

	const modelsBySelectedMakes = useMemo(() => {
		if (!localMake.length || !allModels.length) return [];
		const makesBySlugs = getMakesBySlugs(localMake);
		const makeIds = getMakesIds(makesBySlugs);
		return getModelsByMakeIds(makeIds) || [];
	}, [allModels, getMakesBySlugs, getModelsByMakeIds, localMake]);

	const formattedModels = useMemo(
		() =>
			formatAsSelectOptions({
				options: sortModels(modelsBySelectedMakes),
				text: 'namePretty',
				textSuffix: 'total',
				value: 'id',
			}),
		[modelsBySelectedMakes],
	);

	const handleMakeChange = useCallback(
		({ value }: { value: Array<{ value: string }> }) => {
			const newMakes = mapSelectOptionToFilters(value);
			setLocalMake(newMakes);

			let newModels: number[] = [];
			if (newMakes.length && allModels.length) {
				const makesBySlugs = getMakesBySlugs(newMakes);
				const makeIds = getMakesIds(makesBySlugs);
				newModels = filterModelsByMakes({ makeIds, models: getModelsById(localModel) });
			}
			setLocalModel(newModels);

			if (autoCommit) {
				onCommit?.(newMakes, newModels);
			}
		},
		[allModels, autoCommit, getMakesBySlugs, getModelsById, localModel, onCommit],
	);

	const handleModelChange = useCallback(
		({ value }: { value: Array<{ value: number }> }) => {
			const newModels = mapSelectOptionToFilters(value);
			setLocalModel(newModels);

			if (autoCommit) {
				onCommit?.(localMake, newModels);
			}
		},
		[autoCommit, localMake, onCommit],
	);

	const isModelDisabled = formattedModels.length === 0;
	const makeOptionsChangedCount = useValueChangedCount(formattedMakes);
	const modelOptionsChangedCount = useValueChangedCount(formattedModels);

	const makeValues = useMemo(
		() => (formattedMakes.length ? mapValuesToSelect(localMake, formattedMakes) : []),
		[formattedMakes, localMake],
	);

	const modelValues = useMemo(
		() => (formattedModels.length ? mapValuesToSelect(localModel, formattedModels) : []),
		[formattedModels, localModel],
	);

	return {
		formattedMakes,
		formattedModels,
		handleMakeChange,
		handleModelChange,
		isModelDisabled,
		localMake,
		localModel,
		makeOptionsChangedCount,
		makeValues,
		modelOptionsChangedCount,
		modelValues,
	};
};
